// src/pages/HowItWorks.jsx
import React, { useState } from 'react';
import {
  Box, Container, Typography, Grid, Card, CardContent,
  Chip, Button, Avatar, Stack, Paper, Accordion,
  AccordionSummary, AccordionDetails, alpha, useTheme, useMediaQuery,
} from '@mui/material';
import {
  Fingerprint, ShoppingCart, Handshake, GppGood, Payment,
  LocalShipping, ExpandMore, ArrowRightAlt, RocketLaunch,
  CheckCircle, AccessTime, Public, VerifiedUser, Security,
  SupportAgent, Receipt, TrackChanges, Business, Person,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, delay },
});

const HowItWorks = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expanded, setExpanded] = useState(false);

  const buyerSteps = [
    {
      number: '01', icon: <Fingerprint />, color: '#4caf50',
      title: 'Register & Verify',
      duration: '2 minutes',
      description: 'Create your buyer account and complete KYC business verification to access the marketplace.',
      details: ['Email registration with OTP', 'Business document upload', 'Identity verification', 'Profile setup & preferences'],
    },
    {
      number: '02', icon: <ShoppingCart />, color: '#2196f3',
      title: 'Browse & Enquire',
      duration: 'Instant',
      description: 'Explore thousands of fresh fruit listings from verified exporters worldwide and send enquiries.',
      details: ['Advanced search & filters', 'Compare prices & quality', 'View supplier ratings', 'Send bulk enquiries'],
    },
    {
      number: '03', icon: <Handshake />, color: '#ff9800',
      title: 'Negotiate & Confirm',
      duration: '24–48 hours',
      description: 'Communicate directly with suppliers through our secure messaging system to agree on terms.',
      details: ['Secure in-platform messaging', 'Negotiate price & MOQ', 'Request samples', 'Confirm order details'],
    },
    {
      number: '04', icon: <GppGood />, color: '#e91e63',
      title: 'Quality Inspection',
      duration: 'Pre-shipment',
      description: 'Our certified inspectors verify the quality, grade, and quantity before shipment.',
      details: ['Third-party inspection', 'Grade certification', 'Phytosanitary checks', 'Detailed inspection report'],
    },
    {
      number: '05', icon: <Payment />, color: '#9c27b0',
      title: 'Secure Payment',
      duration: 'Escrow protected',
      description: 'Pay securely via our escrow system — funds released only after successful delivery confirmation.',
      details: ['Multiple currency support', 'Escrow protection', 'Invoice generation', 'Payment tracking'],
    },
    {
      number: '06', icon: <LocalShipping />, color: '#00bcd4',
      title: 'Shipment & Delivery',
      duration: 'Live GPS tracking',
      description: 'Track your shipment in real-time from origin to your port with full customs support.',
      details: ['Customs documentation', 'Real-time GPS tracking', 'Port-to-port updates', 'Delivery confirmation'],
    },
  ];

  const sellerSteps = [
    {
      number: '01', icon: <Business />, color: '#4caf50',
      title: 'Register as Seller',
      duration: '5 minutes',
      description: 'Create your seller profile and get verified to start listing your fruits globally.',
      details: ['Business registration', 'Export license upload', 'Bank account setup', 'Profile optimization'],
    },
    {
      number: '02', icon: <Receipt />, color: '#ff5722',
      title: 'List Your Products',
      duration: 'Instant publish',
      description: 'Add your fruit listings with photos, pricing, certifications, and export capabilities.',
      details: ['Product photos & videos', 'Set price & MOQ', 'Add certifications', 'Seasonal availability'],
    },
    {
      number: '03', icon: <Person />, color: '#ff9800',
      title: 'Receive Enquiries',
      duration: 'Real-time alerts',
      description: 'Get notified instantly when buyers enquire about your products and respond quickly.',
      details: ['Instant notifications', 'Buyer verification badge', 'Chat & negotiate', 'Manage multiple enquiries'],
    },
    {
      number: '04', icon: <GppGood />, color: '#e91e63',
      title: 'Pass Inspection',
      duration: 'Pre-shipment',
      description: 'Prepare your shipment and schedule a quality inspection for buyer confidence.',
      details: ['Schedule inspection', 'Quality grading', 'Documentation support', 'Compliance checks'],
    },
    {
      number: '05', icon: <Security />, color: '#9c27b0',
      title: 'Receive Payment',
      duration: 'Escrow release',
      description: 'Funds held in escrow are released to your account after buyer confirms receipt.',
      details: ['Fast fund release', 'Multiple currencies', 'Bank transfer support', 'Transaction history'],
    },
    {
      number: '06', icon: <TrackChanges />, color: '#00bcd4',
      title: 'Ship & Track',
      duration: 'Real-time tracking',
      description: 'Manage shipping documentation and track delivery status in real-time.',
      details: ['Export documentation', 'Logistics coordination', 'Live shipment tracking', 'After-sales support'],
    },
  ];

  const faqs = [
    { q: 'How long does verification take?', a: 'Buyer verification typically takes 24–48 hours. Seller verification may take up to 3 business days due to export license validation.' },
    { q: 'What is the minimum order quantity (MOQ)?', a: 'MOQ varies by supplier and product. Most listings start from 1 metric ton. You can filter listings by MOQ on the marketplace.' },
    { q: 'How does the escrow payment system work?', a: 'When you place an order, payment is held securely in escrow. Funds are only released to the seller after you confirm receipt of the shipment in satisfactory condition.' },
    { q: 'Which countries do you support?', a: 'We support fruit trade across 150+ countries globally. Our platform handles multi-currency payments and provides customs documentation support for international shipments.' },
    { q: 'What certifications do sellers need?', a: 'Sellers must have valid export licenses and relevant food safety certifications (FSSAI, GlobalGAP, HACCP, or country-specific equivalents).' },
    { q: 'What happens if the quality is not as described?', a: 'Our escrow system protects buyers. If quality does not match the agreed inspection report, you can raise a dispute and our team will mediate. Funds remain in escrow until resolved.' },
    { q: 'How do I contact support?', a: 'Reach us at Support@smartwaveintl.com or call +91 8925851195/96. Our team responds within 10 minutes during business hours.' },
    { q: 'Are there any platform fees?', a: 'Buyers can browse and enquire for free. A small transaction fee applies on confirmed orders. Seller subscription plans are available — contact us for pricing.' },
  ];

  const whyUs = [
    { icon: <VerifiedUser sx={{ fontSize: 36 }} />, color: '#4caf50', title: 'Verified Network', desc: '500+ verified exporters across 60+ countries' },
    { icon: <Security sx={{ fontSize: 36 }} />, color: '#2196f3', title: 'Escrow Protection', desc: '$2B+ in secure transactions processed' },
    { icon: <GppGood sx={{ fontSize: 36 }} />, color: '#ff9800', title: 'Quality Assured', desc: '99% quality pass rate with certified inspections' },
    { icon: <SupportAgent sx={{ fontSize: 36 }} />, color: '#e91e63', title: '24/7 Support', desc: 'Dedicated team with 10-minute response time' },
    { icon: <Public sx={{ fontSize: 36 }} />, color: '#9c27b0', title: 'Global Reach', desc: 'Trade across 150+ countries with ease' },
    { icon: <AccessTime sx={{ fontSize: 36 }} />, color: '#00bcd4', title: 'Fast Processing', desc: '48-hour customs clearance support' },
  ];

  const [activeTab, setActiveTab] = useState('buyer');

  const activeSteps = activeTab === 'buyer' ? buyerSteps : sellerSteps;

  return (
    <Box sx={{ backgroundColor: 'grey.50', minHeight: '100vh' }}>

      {/* ── Hero ── */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0d2f12 0%, #1b5e20 40%, #2e7d32 100%)',
        color: 'white', py: { xs: 8, md: 12 }, position: 'relative', overflow: 'hidden',
      }}>
        {[200, 400, 300].map((size, i) => (
          <Box key={i} sx={{
            position: 'absolute', borderRadius: '50%',
            backgroundColor: alpha('#4caf50', 0.07),
            width: size, height: size,
            top: ['-5%', '40%', '10%'][i], left: ['70%', '-5%', '50%'][i],
            pointerEvents: 'none',
          }} />
        ))}
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <motion.div {...fadeUp()}>
            <Chip label="PLATFORM GUIDE" sx={{ bgcolor: alpha('#ffeb3b', 0.2), color: '#ffeb3b', fontWeight: 700, mb: 2 }} />
            <Typography variant="h2" fontWeight={800} sx={{ mb: 2, fontSize: { xs: '2rem', md: '3.5rem' } }}>
              How Smartwave Works
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.85, maxWidth: 700, mx: 'auto', fontWeight: 400, mb: 4 }}>
              A simple, secure, and transparent process for global fruit trading — from enquiry to delivery.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" gap={1}>
              <Button
                variant={activeTab === 'buyer' ? 'contained' : 'outlined'}
                onClick={() => setActiveTab('buyer')}
                size="large"
                sx={{
                  borderRadius: 3, px: 5, fontWeight: 700,
                  ...(activeTab === 'buyer'
                    ? { background: 'linear-gradient(90deg,#ffeb3b,#ff9800)', color: '#1b5e20' }
                    : { borderColor: alpha('#fff', 0.4), color: 'white' }),
                }}
              >
                I'm a Buyer
              </Button>
              <Button
                variant={activeTab === 'seller' ? 'contained' : 'outlined'}
                onClick={() => setActiveTab('seller')}
                size="large"
                sx={{
                  borderRadius: 3, px: 5, fontWeight: 700,
                  ...(activeTab === 'seller'
                    ? { background: 'linear-gradient(90deg,#ffeb3b,#ff9800)', color: '#1b5e20' }
                    : { borderColor: alpha('#fff', 0.4), color: 'white' }),
                }}
              >
                I'm a Seller
              </Button>
            </Stack>
          </motion.div>
        </Container>
      </Box>

      {/* ── Steps ── */}
      <Container maxWidth="xl" sx={{ py: { xs: 8, md: 12 } }}>
        <motion.div {...fadeUp()}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              {activeTab === 'buyer' ? 'Buyer Journey' : 'Seller Journey'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {activeTab === 'buyer'
                ? 'Follow these 6 simple steps to import fresh fruits from verified global exporters.'
                : 'Follow these 6 steps to start exporting your fruits to global buyers.'}
            </Typography>
          </Box>
        </motion.div>

        <Grid container spacing={3}>
          {activeSteps.map((step, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <motion.div {...fadeUp(index * 0.08)}>
                <Card sx={{
                  height: '100%', borderRadius: 4, p: 1,
                  border: `2px solid ${alpha(step.color, 0.15)}`,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: `0 20px 50px ${alpha(step.color, 0.15)}`,
                    borderColor: step.color,
                  },
                }} elevation={0}>
                  <CardContent sx={{ p: 3 }}>
                    {/* Number + Icon */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{
                        bgcolor: step.color, width: 52, height: 52,
                        fontSize: '1.1rem', fontWeight: 900,
                        boxShadow: `0 8px 20px ${alpha(step.color, 0.35)}`,
                      }}>
                        {step.number}
                      </Avatar>
                      <Box sx={{ color: step.color }}>{step.icon}</Box>
                    </Box>

                    <Typography variant="h6" fontWeight="bold" gutterBottom>{step.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{step.description}</Typography>

                    {/* Duration */}
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, borderRadius: 10, bgcolor: alpha(step.color, 0.1), mb: 2 }}>
                      <AccessTime sx={{ fontSize: 14, color: step.color }} />
                      <Typography variant="caption" fontWeight="bold" sx={{ color: step.color }}>{step.duration}</Typography>
                    </Box>

                    {/* Details */}
                    <Stack spacing={0.5}>
                      {step.details.map((d, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle sx={{ fontSize: 14, color: step.color }} />
                          <Typography variant="caption" color="text.secondary">{d}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── Why Choose Us ── */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="xl">
          <motion.div {...fadeUp()}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip label="WHY SMARTWAVE" sx={{ bgcolor: alpha('#4caf50', 0.1), color: 'primary.main', fontWeight: 700, mb: 2 }} />
              <Typography variant="h3" fontWeight="bold" gutterBottom>Built for Global Trade</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                Everything you need to trade fresh fruits confidently across borders
              </Typography>
            </Box>
          </motion.div>
          <Grid container spacing={3}>
            {whyUs.map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <motion.div {...fadeUp(i * 0.08)}>
                  <Paper sx={{
                    p: 3, borderRadius: 4, textAlign: 'center', height: '100%',
                    border: `1px solid ${alpha(item.color, 0.15)}`,
                    transition: 'all 0.3s',
                    '&:hover': { boxShadow: `0 12px 40px ${alpha(item.color, 0.15)}`, transform: 'translateY(-4px)' },
                  }} elevation={0}>
                    <Avatar sx={{ bgcolor: alpha(item.color, 0.12), color: item.color, width: 70, height: 70, mx: 'auto', mb: 2 }}>
                      {item.icon}
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>{item.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── FAQ ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <motion.div {...fadeUp()}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip label="FAQ" sx={{ bgcolor: alpha('#ff9800', 0.1), color: '#ff9800', fontWeight: 700, mb: 2 }} />
            <Typography variant="h3" fontWeight="bold" gutterBottom>Frequently Asked Questions</Typography>
            <Typography variant="body1" color="text.secondary">Everything you need to know before you start trading</Typography>
          </Box>
        </motion.div>
        <Stack spacing={2}>
          {faqs.map((faq, i) => (
            <motion.div key={i} {...fadeUp(i * 0.05)}>
              <Accordion
                expanded={expanded === i}
                onChange={() => setExpanded(expanded === i ? false : i)}
                elevation={0}
                sx={{
                  border: '1px solid', borderColor: 'divider', borderRadius: '12px !important',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { borderColor: 'primary.main', boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}` },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5 }}>
                  <Typography fontWeight="600">{faq.q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>{faq.a}</Typography>
                </AccordionDetails>
              </Accordion>
            </motion.div>
          ))}
        </Stack>
      </Container>

      {/* ── CTA ── */}
      <Container maxWidth="xl" sx={{ pb: 10 }}>
        <motion.div {...fadeUp()}>
          <Paper sx={{
            background: 'linear-gradient(135deg,#1b5e20 0%,#2e7d32 100%)',
            color: 'white', p: { xs: 5, md: 8 }, borderRadius: 4, textAlign: 'center',
          }}>
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Ready to Start Trading?
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 4, maxWidth: 600, mx: 'auto', fontWeight: 400 }}>
              Join thousands of fruit traders already growing their business with Smartwave International.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                component={Link} to="/register"
                variant="contained" size="large"
                startIcon={<RocketLaunch />}
                sx={{ bgcolor: '#ffeb3b', color: '#1b5e20', fontWeight: 'bold', borderRadius: 3, px: 5 }}
              >
                Get Started Free
              </Button>
              <Button
                component={Link} to="/contact"
                variant="outlined" size="large"
                endIcon={<ArrowRightAlt />}
                sx={{ borderColor: alpha('#fff', 0.5), color: 'white', borderRadius: 3, px: 5 }}
              >
                Contact Our Team
              </Button>
            </Stack>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default HowItWorks;